import PropTypes from 'prop-types';
import { createContext, useState, useCallback, useEffect, useRef } from 'react';
import useTableSettings from 'hooks/use-table-settings';
import { useIdentity } from 'base/identity-resolver';
import { fetchTasksByAppliedFilters, fetchAllTasksCount } from 'apps/tasks/utils/fetch-helpers';
import { matchesDateFilter } from 'apps/cadences-v2/components/date-filter-popover';

const TasksContext = createContext(() => {});

const TasksProvider = ({ referrer, id, children }) => {
  const TASK_LIMIT = 25;
  const filtersDefaultValue = {
    taskCompletion: { label: 'Incomplete Tasks', value: ['IN_PROGRESS', 'PAUSED'] },
    actionTypes: [],
    assignees: [],
    taskCreators: [],
    isCadenceTask: undefined,
    taskTypes: [],
    constituents: [],
  };
  const { user } = useIdentity();

  const getDefaultFilters = useCallback(() => {
    if (referrer === 'contact') {
      return {
        target_contact_id: id,
        statuses: ['IN_PROGRESS', 'PAUSED'],
      };
    } else {
      if (children.props.userType === 'OWNER') {
        return {
          owner_user_id: user.id,
          statuses: ['IN_PROGRESS', 'PAUSED'],
        };
      } else if (children.props.userType === 'CREATOR_BUT_NOT_OWNER') {
        return {
          creator_user_id: user.id,
          statuses: ['IN_PROGRESS', 'PAUSED'],
          exclude: [{ field: 'owner_user_id', value: user.id }],
        };
      }
    }
  }, [children.props.userType, id, referrer, user.id]);

  const { tableSettings, updateTableSettings, resetTable } = useTableSettings({
    sortProp: 'current_due_date',
    page: 1,
    sortReverse: false,
  });
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [allTasksCount, setAllTasksCount] = useState();
  const [allTasksCountLoading, setAllTasksCountLoading] = useState();
  const [removedLastTask, setRemovedLastTask] = useState(false);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const [limit, setLimit] = useState(TASK_LIMIT);
  const [taskFilters, setTaskFilters] = useState(getDefaultFilters()); //filters object for gql query
  const [filters, setFilters] = useState(filtersDefaultValue); //filters that represent components in the UI
  const [groupBy, setGroupBy] = useState('none'); // 'none' | 'cadence_step'
  const [groupSortOrder, setGroupSortOrder] = useState('desc'); // 'desc' = most tasks first
  const preGroupLimitRef = useRef(TASK_LIMIT);
  const [constituentSearch, setConstituentSearch] = useState([]);
  const defaultDateFilter = { operator: 'all', date1: null, date2: null };
  const [completedDateRange, setCompletedDateRange] = useState(defaultDateFilter);
  const [creationDateRange, setCreationDateRange] = useState(defaultDateFilter);
  const [dueDateRange, setDueDateRange] = useState(defaultDateFilter);
  // Client-side task type filter — applied after API fetch to distinguish
  // cadence 2.0 tasks (task_source='cadence_v2') which the backend can't filter.
  const [taskTypeClientFilter, setTaskTypeClientFilter] = useState(null);
  // Separate client-side filter for cadence name selection (won't conflict with taskType filter).
  const [cadenceNameFilter, setCadenceNameFilter] = useState(null);

  const getAndSetTasks = useCallback(
    async (taskFilters) => {
      const { sortReverse, sortProp } = tableSettings;
      setLoading(true);

      const hasClientFilter =
        taskTypeClientFilter ||
        cadenceNameFilter ||
        constituentSearch.length > 0 ||
        completedDateRange?.operator !== 'all' ||
        creationDateRange?.operator !== 'all' ||
        dueDateRange?.operator !== 'all';

      try {
        // When client-side filtering is active, we must fetch ALL tasks from the API
        // first, then filter and paginate on the client. Otherwise the server returns
        // one page (e.g. 25) and the client filter discards some, giving incomplete results.
        const fetchLimit = hasClientFilter ? 10000 : limit;
        const fetchOffset = hasClientFilter ? 0 : offset;

        const tasks = await fetchTasksByAppliedFilters({
          ...taskFilters,
          offset: fetchOffset,
          limit: fetchLimit,
          sort: [sortProp, sortReverse ? 'DESC' : 'ASC'],
        });

        let items = taskTypeClientFilter ? tasks.items.filter(taskTypeClientFilter) : tasks.items;
        if (cadenceNameFilter) items = items.filter(cadenceNameFilter);
        if (constituentSearch.length > 0) {
          const selectedIds = new Set(constituentSearch.map((c) => String(c.value)));
          items = items.filter((t) => {
            const contactId = t.contact?.id || t.target_contact_id;
            return contactId && selectedIds.has(String(contactId));
          });
        }
        if (completedDateRange?.operator !== 'all') {
          // completed_at is epoch seconds (fetch-helpers converts it), convert back to ISO for matchesDateFilter
          items = items.filter((t) => {
            const dateStr = t.completed_at ? new Date(t.completed_at * 1000).toISOString() : null;
            return matchesDateFilter(dateStr, completedDateRange);
          });
        }
        if (creationDateRange?.operator !== 'all') {
          items = items.filter((t) => matchesDateFilter(t.created_at, creationDateRange));
        }
        if (dueDateRange?.operator !== 'all') {
          items = items.filter((t) => matchesDateFilter(t.current_due_date, dueDateRange));
        }

        if (hasClientFilter) {
          // Client-side pagination: slice the filtered results to the current page
          const totalFiltered = items.length;
          const pageItems = items.slice(offset, offset + limit);
          setTasks(pageItems);
          setTotal(totalFiltered);
        } else {
          setTasks(items);
          setTotal(tasks.total);
        }
        setLoading(false);
      } catch {
        setLoading(false);
      }
    },
    [
      offset,
      tableSettings,
      limit,
      taskTypeClientFilter,
      cadenceNameFilter,
      constituentSearch,
      completedDateRange,
      creationDateRange,
      dueDateRange,
    ]
  );

  const removeTaskFromState = async (task) => {
    if (tasks.length === 1) {
      setRemovedLastTask(true);
      setAllTasksCount(0);
    }

    const updatePagination = (tasks) => {
      if (tasks.length === 0 && tableSettings.page > 1) {
        updateTableSettings({ page: tableSettings.page - 1 });
      }
    };

    const handleReserveTasks = async (tasks) => {
      const tasksLength = tasks.length;
      if (!(tasksLength % 50) && total > tasksLength) {
        const nextTasks = await fetchTasksByAppliedFilters({
          ...taskFilters,
          offset: offset + tasksLength,
          limit: limit,
          sort: [tableSettings.sortProp, tableSettings.sortReverse ? 'DESC' : 'ASC'],
        });

        setTasks([...tasks, ...nextTasks.items]);
      }
    };

    setTotal(total - 1);

    const filteredTasks = tasks.filter((t) => t.id !== task.id);
    setTasks(filteredTasks);

    updatePagination(filteredTasks);
    if (total - 1 > 0) handleReserveTasks(filteredTasks);
  };

  const getAllTasksCount = useCallback(async () => {
    setAllTasksCountLoading(true);
    try {
      const resp = await fetchAllTasksCount({
        user_id: user.id,
      });
      setAllTasksCount(resp.total);
      setAllTasksCountLoading(false);
    } catch {
      setAllTasksCountLoading(false);
    }
  }, [user.id]);

  const updateTaskFilters = (filters) => {
    setTaskFilters(filters);
    updateTableSettings({ page: 1 });
  };

  // When grouping is active, fetch all tasks so groups are complete
  useEffect(() => {
    if (groupBy === 'cadence_step') {
      preGroupLimitRef.current = limit;
      setLimit(5000);
      setOffset(0);
    } else {
      setLimit(preGroupLimitRef.current);
      setOffset(0);
    }
  }, [groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getAllTasksCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TasksContext.Provider
      value={{
        tableSettings,
        updateTableSettings,
        resetTable,
        tasks,
        total,
        loading,
        setLoading,
        removeTaskFromState,
        getAndSetTasks,
        setTasks,
        userId: user.id,
        referrer,
        taskFilters,
        setTaskFilters,
        getDefaultFilters,
        filters,
        setFilters,
        filtersDefaultValue,
        allTasksCount,
        setAllTasksCount,
        allTasksCountLoading,
        removedLastTask,
        updateTaskFilters,
        groupBy,
        setGroupBy,
        groupSortOrder,
        setGroupSortOrder,
        constituentSearch,
        setConstituentSearch,
        completedDateRange,
        setCompletedDateRange,
        creationDateRange,
        setCreationDateRange,
        dueDateRange,
        setDueDateRange,
        limit,
        setLimit,
        offset,
        setOffset,
        setTaskTypeClientFilter,
        setCadenceNameFilter,
      }}
    >
      {children}
    </TasksContext.Provider>
  );
};

TasksProvider.propTypes = {
  children: PropTypes.any,
  referrer: PropTypes.string,
  id: PropTypes.number,
};

export { TasksProvider, TasksContext };
